import { naturalCompare, type NoteIndexEntry } from '@kartothek/shared';

/** Anker-ID für Überschriften (für `[[Notiz#Überschrift]]`). */
export function slugify(text: string): string {
  return (
    'h-' +
    text
      .normalize('NFC')
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
  );
}

/** Zerlegt ein Linkziel `Ordner/Titel` in Ordner und Titel. */
export function splitTarget(target: string): { folder: string; title: string } {
  const clean = target.replace(/\.md$/i, '').replace(/^\/+/, '');
  const i = clean.lastIndexOf('/');
  return i === -1 ? { folder: '', title: clean } : { folder: clean.slice(0, i), title: clean.slice(i + 1) };
}

/**
 * Einfache unscharfe Suche für Strg+K: Präfix > Wortanfang > Teilstring > Teilfolge.
 * Gibt die besten Treffer sortiert zurück.
 */
export function fuzzyFilter<T extends Pick<NoteIndexEntry, 'title'> & { folder?: string; updatedAt?: string }>(
  items: readonly T[],
  query: string,
  limit = 50,
): T[] {
  const q = query.normalize('NFC').toLowerCase().trim();
  if (!q) return [...items].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')).slice(0, limit);
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const title = item.title.toLowerCase();
    const full = (item.folder ? item.folder + '/' : '') + title;
    let score = 0;
    if (title === q) score = 1000;
    else if (title.startsWith(q)) score = 800;
    else if (new RegExp(`(^|[\\s\\-–.(/])${escapeRegex(q)}`).test(title)) score = 600;
    else if (title.includes(q)) score = 400;
    else if (full.toLowerCase().includes(q)) score = 300;
    else {
      let i = 0;
      for (const ch of title) if (ch === q[i]) i++;
      if (i === q.length) score = 100;
    }
    if (score > 0) scored.push({ item, score: score - title.length * 0.1 });
  }
  return scored
    .sort((a, b) => b.score - a.score || naturalCompare(a.item.title, b.item.title))
    .slice(0, limit)
    .map((s) => s.item);
}

export function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function classNames(...xs: (string | false | null | undefined)[]): string {
  return xs.filter(Boolean).join(' ');
}
